import { useCallback, useState } from "react";
import request from "superagent";
import { ListItem } from "@avinet/adaptive-ui-core/form/controls/List";
import List from "@avinet/adaptive-ui-core/form/controls/List";
import FormProvider from "@avinet/adaptive-ui-core/form/FormProvider";
import useForm from "@avinet/adaptive-ui-core/form/useForm";
import Grow from "@avinet/adaptive-ui-core/layout/Grow";
import Row from "@avinet/adaptive-ui-core/layout/Row";
import Dialog from "@avinet/adaptive-ui-core/ui/Dialog";
import { useTranslate } from "@avinet/adaptive-ui-translate";
import { BACKEND_URL } from "../../config/config";
import useSessionToken from "../../hooks/useSessionToken";
import { isStoredSection } from "../../types/Section";
import Survey from "../../types/Survey";
import { Icon } from "../Icon";
import { LoadingMask } from "../Loading";

const downloadOptions: ListItem[] = [
  { value: "xlsx", label: "Excel" },
  { value: "csv", label: "CSV" },
];

interface ResultsDownloadForm {
  format: string;
}

const ResultsDownload = ({
  survey,
  onClose,
}: {
  onClose: () => void;
  survey: Survey;
}) => {
  const t = useTranslate("Results");
  const sessionToken = useSessionToken();
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState(false);

  const handleSubmit = useCallback(
    (data: ResultsDownloadForm) => {
      if (!data.format || !sessionToken) return;

      setError(false);
      setDownloading(true);

      request
        .post(BACKEND_URL + "WebServices/client/DataViewExport.asmx/Export")
        .set("gm_session_id", sessionToken)
        .send({
          request: {
            theme_uuid: survey.uuid,
            columnOrder: [
              "id",
              "date_created",
              "date_modified",
              ...survey.schema
                .map((section) =>
                  isStoredSection(section) ? section.columnName : null
                )
                .filter((c) => !!c),
              "created_by",
              "modified_by",
            ],
            format: data.format,
            srid: 4326,
          },
        })
        .then((res) => {
          setDownloading(false);
          if (!res.body.d.success) {
            setError(true);
            return;
          }

          try {
            const dict = res.body.d.data as { key: string; value: string }[];
            const url = dict.filter((kv) => kv.key === "download_url")[0].value;

            const a = document.createElement("A") as HTMLAnchorElement;
            a.href = BACKEND_URL + url;
            a.download = "download.zip";
            a.click();

            onClose();
          } catch (ex) {
            console.error(ex);
            setError(true);
          }
        })
        .catch((err) => {
          console.error(err);
          setDownloading(false);
          setError(true);
        });
    },
    [survey, sessionToken, onClose]
  );

  const { form, state } = useForm<ResultsDownloadForm>({
    onSubmit: handleSubmit,
  });

  const format = form.getValue("format");

  return (
    <Dialog title={t("download")} onClose={onClose}>
      <FormProvider form={form} state={state}>
        <form onSubmit={form.submit}>
          <List
            layout="vertical"
            id="format"
            items={downloadOptions}
            required
          />
          {error && <p className="error">{t("downloadError")}</p>}
          <Row>
            <Grow />
            <button className="btn" disabled={!format}>
              <Icon name="arrowDownload" /> {t("download")}
            </button>
          </Row>

          {downloading && <LoadingMask />}
        </form>
      </FormProvider>
    </Dialog>
  );
};

export default ResultsDownload;
